import {
  type TelemetryContext,
  useTelemetry,
} from '@ctw/shared/context/telemetry/telemetry-boundary';
import type { TelemetryUserInteraction } from '@ctw/shared/context/telemetry/user-interactions';
import { type RouterOptions, useZuiContext } from '@ctw/shared/context/zui-provider';
import { type ClassName, twx } from '@ctw/shared/utils/tailwind';
import { type PropsWithChildren, type Ref, useCallback, useMemo } from 'react';

const isExternalHref = (href: string): boolean => {
  try {
    const url = new URL(href);

    if (url.hostname !== window.location.hostname) {
      return true;
    }
  } catch (_error) {
    /* pass */
  }

  return false;
};

export const anchorElementClassName =
  'underline-offset-[3px] rounded-md fill-primary-text text-primary-text no-underline hover:underline focus:no-underline cursor-pointer font-sans font-medium focus-visible:outline-4 focus-visible:outline-offset-[3px] focus-visible:outline-primary-main active:scale-[.98]';

export interface LinkProps extends PropsWithChildren {
  ref?: Ref<HTMLAnchorElement>;
  href: string;
  id?: string;
  rel?: HTMLAnchorElement['target'];
  target?: '_self' | '_blank' | '_parent' | '_top' | '_unfencedTop';
  dataTestId?: string;
  ariaLabel?: string;
  telemetryInteractionName?: TelemetryUserInteraction;
  telemetryContext?: TelemetryContext;
  telemetryObfuscatedIdentifier?: string;
  routerOptions?: RouterOptions;
  className?: ClassName;
  dataSearchIndex?: string;
  onClick?: () => void;
}

export const Link = ({
  ref,
  id,
  href: rawHref,
  ariaLabel,
  target,
  routerOptions,
  className,
  dataTestId,
  telemetryInteractionName = 'link_click',
  telemetryContext = {},
  telemetryObfuscatedIdentifier,
  dataSearchIndex,
  onClick: externalOnClick,
  children,
}: LinkProps) => {
  const { navigate, pathname } = useZuiContext();
  const telemetry = useTelemetry();
  const href = useMemo(
    () =>
      rawHref.startsWith('..')
        ? new URL(rawHref, `${window.location.origin}${pathname}`).pathname
        : rawHref,
    [pathname, rawHref],
  );
  const isExternal = useMemo(() => isExternalHref(href), [href]);

  const onClick = useCallback(() => {
    telemetry.trackInteraction(telemetryInteractionName, {
      ...telemetryContext,
      isExternal,
      linkText: `<${telemetryObfuscatedIdentifier ?? 'REDACTED'}>`,
    });
    externalOnClick?.();
  }, [
    externalOnClick,
    telemetry,
    isExternal,
    telemetryObfuscatedIdentifier,
    telemetryInteractionName,
    telemetryContext,
  ]);

  if (isExternal) {
    return (
      <a
        id={id}
        ref={ref}
        onClick={onClick}
        data-testid={dataTestId}
        data-search-index={dataSearchIndex}
        aria-label={ariaLabel}
        href={href}
        target={target}
        rel="noreferrer noopener"
        className={twx(anchorElementClassName, className)}
      >
        {children}
      </a>
    );
  }

  return (
    <a
      id={id}
      ref={ref}
      data-testid={dataTestId}
      data-search-index={dataSearchIndex}
      aria-label={ariaLabel}
      onClick={(event) => {
        onClick?.();
        if (target !== '_blank') {
          event.preventDefault();
          navigate(href, routerOptions);
        }
      }}
      href={href}
      target={target}
      className={twx(anchorElementClassName, className)}
    >
      {children}
    </a>
  );
};
